import { useSyncExternalStore } from 'react';

/**
 * The one set of breakpoints the JS side may branch on. They are Tailwind's defaults, so a class like `lg:flex`
 * and `useIsDesktop()` always agree.
 *   < sm (640)     phone
 *   sm .. lg-1     tablet: the app shell is still the mobile drawer / single-pane chat
 *   >= lg (1024)   desktop shell: static sidebar, split chat + workspace
 * `md:` utilities are for density tweaks only, never for switching layout modes.
 */
export const BREAKPOINTS = { sm: 640, md: 768, lg: 1024, xl: 1280 } as const;

function subscribe(query: string, onChange: () => void): () => void {
  const mq = window.matchMedia(query);
  mq.addEventListener('change', onChange);
  return () => mq.removeEventListener('change', onChange);
}

/** Reactive `window.matchMedia(query).matches`. Server-safe: reports false when there is no window. */
export function useMediaQuery(query: string): boolean {
  return useSyncExternalStore(
    (onChange) => (typeof window === 'undefined' ? () => {} : subscribe(query, onChange)),
    () => (typeof window === 'undefined' ? false : window.matchMedia(query).matches),
    () => false,
  );
}

/** True at the `lg` breakpoint and up: the desktop shell and two-column chat layout. */
export function useIsDesktop(): boolean {
  return useMediaQuery(`(min-width: ${BREAKPOINTS.lg}px)`);
}
